/**
 * Proofing Flow State Calculator
 *
 * Computes the runtime state of the proofing flow from dynamic template data.
 * All logic is data-driven — no hardcoded requirement types.
 */

import {
  ProofTarget,
  type StoreFlowState,
  type StoreStepState,
  type ProductStepState,
  type ProductRequirementState,
  type ProductRequirement,
} from "./types";
import { sortProductRequirements } from "./config";

/**
 * Product requirement with proof status (from RPC response)
 */
export interface ProductRequirementWithProof extends ProductRequirement {
  proof: {
    proof_id: number;
    status: "pending" | "approved" | "rejected";
    image_url: string;
    created_at: string;
  } | null;
}

/**
 * Store activity data from RPC
 */
export interface StoreActivityData {
  store_activity_id: number;
  is_fulfilled: boolean;
  product_id: number | null;
  product_name: string | null;
  period_id: number;
  requirement_template_id: number;
  require_store: boolean;
  product_requirements: ProductRequirementWithProof[];
}

/**
 * Store proof requirements from RPC
 */
export interface StoreRequirementsData {
  store: {
    id: number;
    name: string;
    latitude: number | null;
    longitude: number | null;
  };
  store_proof: {
    proof_id: number;
    status: "pending" | "approved" | "rejected";
    image_url: string;
    created_at: string;
  } | null;
  store_activities: StoreActivityData[];
}

/**
 * Check if a proof is active (pending or approved)
 */
function isProofActive(proof: ProductRequirementWithProof["proof"]): boolean {
  return !!proof && (proof.status === "pending" || proof.status === "approved");
}

/**
 * Calculate if any store activity at this store requires a store proof.
 * Store proof is required if ANY SA's template has require_store = true.
 */
function storeProofIsRequired(activities: StoreActivityData[]): boolean {
  return activities.some((a) => a.require_store);
}

/**
 * Calculate the state of the store step
 */
function calculateStoreStepState(
  storeProof: StoreRequirementsData["store_proof"],
  activities: StoreActivityData[],
): StoreStepState {
  const isRequired = storeProofIsRequired(activities);
  const isFulfilled = isProofActive(storeProof);

  return {
    target: ProofTarget.STORE,
    isRequired,
    isFulfilled,
    isBlocked: false, // Store step is never blocked (always first)
  };
}

/**
 * Calculate the state of a single product step
 */
function calculateProductStepState(
  activity: StoreActivityData,
  isStoreStepComplete: boolean,
  storeProofRequired: boolean,
): ProductStepState {
  // Step is blocked if store proof is required but not complete
  const stepIsBlocked = storeProofRequired && !isStoreStepComplete;

  // Sort requirements by order (cast to maintain proof data)
  const sortedRequirements = sortProductRequirements(
    activity.product_requirements,
  ) as ProductRequirementWithProof[];

  // Calculate requirement states with sequential blocking
  // Uses a for-loop so we can reference the previous element's state
  const requirements: ProductRequirementState[] = [];
  for (let index = 0; index < sortedRequirements.length; index++) {
    const req = sortedRequirements[index];
    const isFulfilled = isProofActive(req.proof);

    // Blocked if step is blocked, OR if previous requirement (by order) is not fulfilled
    let isBlocked = stepIsBlocked;
    if (index > 0) {
      const previousReq = requirements[index - 1];
      isBlocked = isBlocked || !previousReq.isFulfilled;
    }

    requirements.push({
      productRequirement: {
        id: req.id,
        code: req.code,
        description: req.description,
        labels: req.labels,
        order: req.order,
      },
      isFulfilled,
      isBlocked,
    });
  }

  // Step is complete when all requirements are fulfilled
  const isComplete = requirements.every((r) => r.isFulfilled);

  return {
    target: ProofTarget.PRODUCT,
    storeActivityId: activity.store_activity_id,
    productName: activity.product_name || "Unknown Product",
    isComplete,
    isBlocked: stepIsBlocked,
    requirements,
  };
}

/**
 * Count total pictures needed
 *
 * Badge shows total work remaining, not just actionable items.
 * - Store proof: counts as 1 if required and not fulfilled
 * - Product proofs: count all unfulfilled requirements across all activities
 */
function countPicturesNeeded(
  storeStep: StoreStepState,
  productSteps: ProductStepState[],
): number {
  let count = 0;

  // Count store proof if required and not fulfilled
  if (storeStep.isRequired && !storeStep.isFulfilled) {
    count++;
  }

  // Count unfulfilled product requirements
  for (const step of productSteps) {
    for (const req of step.requirements) {
      if (!req.isFulfilled) {
        count++;
      }
    }
  }

  return count;
}

/**
 * Calculate the complete flow state for a store
 */
export function calculateStoreFlowState(
  requirements: StoreRequirementsData | null,
  distance: number | null,
): StoreFlowState | null {
  if (!requirements) return null;

  const storeStep = calculateStoreStepState(
    requirements.store_proof,
    requirements.store_activities,
  );

  const productSteps = (requirements.store_activities || [])
    .slice()
    .sort((a, b) => (a.product_name || "").localeCompare(b.product_name || ""))
    .map((activity) =>
      calculateProductStepState(
        activity,
        storeStep.isFulfilled,
        storeStep.isRequired,
      ),
    );

  const totalPicturesNeeded = countPicturesNeeded(storeStep, productSteps);
  const isComplete =
    (!storeStep.isRequired || storeStep.isFulfilled) &&
    productSteps.every((s) => s.isComplete);

  return {
    storeId: requirements.store.id,
    storeName: requirements.store.name,
    distance,
    totalPicturesNeeded,
    isComplete,
    storeStep,
    productSteps,
  };
}
